import { Injectable, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, interval, Subscription, firstValueFrom } from 'rxjs';
import { GlobalService } from './global.service';
import { AuthService } from './auth.service';

export interface SessionState {
  isExpiring: boolean;
  secondsRemaining: number;
  showWarning: boolean;
}

@Injectable({ providedIn: 'root' })
export class SessionService implements OnDestroy {
  private checkInterval: Subscription | null = null;

  // Configurações
  private warningThresholdSeconds = 120; // 2 minutos antes de expirar, mostrar aviso
  private checkIntervalMs = 1000; // Verificar a cada 1 segundo para countdown fluido

  // Estado da sessão
  public sessionState$ = new BehaviorSubject<SessionState>({
    isExpiring: false,
    secondsRemaining: 0,
    showWarning: false,
  });
  public isWarningVisible = false;
  public secondsRemaining = 0;

  // Renovação automática baseada em atividade
  private lastAutoRenewalTime = 0;
  private readonly AUTO_RENEWAL_THRESHOLD = 1800; // Renovar se faltam menos de 30 min
  private readonly MIN_RENEWAL_INTERVAL = 5 * 60 * 1000; // Mínimo 5 min entre renovações automáticas
  private isAutoRenewing = false;

  // Eventos de atividade
  private activityEvents = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];
  private activityHandler = () => this.updateActivity();

  constructor(
    private globalService: GlobalService,
    private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.setupActivityTracking();
  }

  /**
   * Inicia a monitorização da sessão
   */
  startMonitoring(): void {
    if (this.checkInterval) {
      return; // Já está a monitorizar
    }

    // console.log('[SessionService] A iniciar monitorização de sessão');

    this.checkInterval = interval(this.checkIntervalMs).subscribe(() => {
      this.checkSessionStatus();
    });
  }

  /**
   * Para a monitorização
   */
  stopMonitoring(): void {
    // Unsubscribe do interval
    if (this.checkInterval) {
      this.checkInterval.unsubscribe();
      this.checkInterval = null;
    }

    // Não é necessário gerir destroy$ aqui pois o interval era o único consumidor

    // console.log('[SessionService] A parar monitorização');
    this.isWarningVisible = false;
  }

  /**
   * Configura tracking de atividade do utilizador
   */
  private setupActivityTracking(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.activityEvents.forEach(eventName => {
        window.addEventListener(eventName, this.activityHandler, { passive: true } as AddEventListenerOptions);
      });
    }
  }

  private removeActivityTracking(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.activityEvents.forEach(eventName => {
        window.removeEventListener(eventName, this.activityHandler);
      });
    }
  }

  /**
   * Atualiza - fecha aviso se visível.
   * Renova automaticamente o token se está perto de expirar e o utilizador está ativo.
   */
  updateActivity(): void {
    if (this.isWarningVisible) {
      this.isWarningVisible = false;
    }

    // Calcular o tempo real restante ANTES de decidir auto-renovar
    // (evita usar state antigo caso a tab estivesse inativa/em background)
    const token = this.globalService.loggedUserClass?.Token;
    if (token) {
      const decoded = this.decodeToken(token);
      if (decoded && decoded.exp) {
        const nowInSecs = Math.floor(Date.now() / 1000);
        this.secondsRemaining = Math.max(0, decoded.exp - nowInSecs);
      }
    }

    // Renovação automática: se o token está perto de expirar e há atividade, renovar silenciosamente
    const now = Date.now();
    if (
      !this.isAutoRenewing &&
      this.secondsRemaining > 0 &&
      this.secondsRemaining < this.AUTO_RENEWAL_THRESHOLD &&
      now - this.lastAutoRenewalTime > this.MIN_RENEWAL_INTERVAL
    ) {
      this.isAutoRenewing = true;
      this.lastAutoRenewalTime = now;
      console.log(
        `[SessionService] Renovação automática do token (${this.secondsRemaining}s restantes, atividade detectada)`,
      );
      this.renewSession()
        .then(() => {
          this.isAutoRenewing = false;
        })
        .catch(() => {
          this.isAutoRenewing = false;
        });
    }
  }

  // Debug mode
  private isDebugActive = false;

  /**
   * Verifica o estado da sessão
   */
  private checkSessionStatus(): void {
    // Se estiver em modo debug, não sobrescreve o estado falso
    if (this.isDebugActive) return;

    const token = this.globalService.loggedUserClass?.Token;

    if (!token) {
      this.stopMonitoring();
      return;
    }

    try {
      const decoded = this.decodeToken(token);

      if (!decoded || !decoded.exp) {
        return;
      }

      const now = Math.floor(Date.now() / 1000);
      const expirationTime = decoded.exp;
      const secondsUntilExpiry = expirationTime - now;

      this.secondsRemaining = Math.max(0, secondsUntilExpiry);

      // Verificar se devemos mostrar aviso
      const shouldShowWarning = secondsUntilExpiry > 0 && secondsUntilExpiry <= this.warningThresholdSeconds;

      if (shouldShowWarning !== this.isWarningVisible) {
        this.isWarningVisible = shouldShowWarning;
      }

      // Emitir estado
      this.sessionState$.next({
        isExpiring: shouldShowWarning,
        secondsRemaining: this.secondsRemaining,
        showWarning: this.isWarningVisible,
      });

      // Se expirou, fazer logout
      if (secondsUntilExpiry <= 0) {
        // console.log('[SessionService] Sessão expirada. Fazendo logout.');
        this.stopMonitoring();
        this.authService.logout();
      }
    } catch (error) {
      console.error('[SessionService] Erro ao verificar token:', error);
    }
  }

  /**
   * Ativar aviso para efeitos de teste/demo
   */
  debugTriggerWarning(): void {
    // console.log('[SessionService] DEBUG: Triggering warning manually');
    this.isDebugActive = true;
    this.secondsRemaining = 65; // 1 min 5 seg
    this.isWarningVisible = true;

    this.sessionState$.next({
      isExpiring: true,
      secondsRemaining: 65,
      showWarning: true,
    });

    // Countdown falso para simulação realista
    let debugCountdown = 65;
    const intervalId = setInterval(() => {
      if (!this.isDebugActive || debugCountdown <= 0) {
        clearInterval(intervalId);
        return;
      }
      debugCountdown--;
      this.secondsRemaining = debugCountdown;
      this.sessionState$.next({
        isExpiring: true,
        secondsRemaining: debugCountdown,
        showWarning: true,
      });
    }, 1000);

    // Desativar debug após 30 segundos para voltar ao normal
    setTimeout(() => {
      this.isDebugActive = false;
      this.isWarningVisible = false; // Forçar fecho se ainda aberto
      clearInterval(intervalId);
      // O próximo tick do intervalo real vai restaurar o estado correto
    }, 30000);
  }

  /**
   * Decodifica o token JWT manualmente (sem biblioteca externa)
   */
  private decodeToken(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      // Decodificar payload (segunda parte)
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  /**
   * Renova a sessão fazendo refresh do token JWT
   */
  async renewSession(): Promise<boolean> {
    try {
      //   console.log('[SessionService] A renovar sessão e token...');

      // Fazer refresh do token JWT (usando firstValueFrom em vez de toPromise deprecated)
      const result = await firstValueFrom(this.authService.refreshToken());

      if (result && result.Token) {
        this.isWarningVisible = false;
        this.isDebugActive = false; // Cancelar modo debug se estiver ativo
        this.updateActivity();
        // console.log('[SessionService] Sessão e token renovados com sucesso');
        return true;
      } else {
        console.error('[SessionService] Refresh token não retornou token válido');
        return false;
      }
    } catch (error) {
      console.error('[SessionService] Erro ao renovar sessão:', error);
      // Se falhar o refresh, fazer logout
      this.authService.logout();
      return false;
    }
  }

  /**
   * Obtém tempo restante formatado MM:SS
   */
  getFormattedTimeRemaining(): string {
    const minutes = Math.floor(this.secondsRemaining / 60);
    const seconds = this.secondsRemaining % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Configura threshold de aviso (em segundos)
   */
  setWarningThreshold(seconds: number): void {
    this.warningThresholdSeconds = seconds;
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
    this.removeActivityTracking();
    // this.sessionState$.complete(); // REMOVIDO: Evitar ObjectUnsubscribedError em Singleton
  }
}
